import { Link } from "@tanstack/react-router";

import { pathFor, useLocale, type Locale } from "@/lib/i18n";

/** Förtroendepunkter – lista på mobil, rad på desktop. */
export function TrustRow({ items }: { items: string[] }) {
  return (
    <section className="border-y border-border bg-sand">
      <ul className="mx-auto grid max-w-6xl gap-x-10 gap-y-2.5 px-5 py-6 text-[0.7rem] tracking-[0.12em] text-muted-foreground uppercase sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center lg:justify-center lg:text-xs">
      {items.map((item) => (
          <li key={item} className="flex items-start gap-2">
            <span aria-hidden className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-primary lg:hidden" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export type ComparisonRow = { row: string; us: string; other: string };

const COMPARISON_HEADERS: Record<Locale, { us: string; other: string }> = {
  sv: { us: "Hos oss", other: "Lokal eller internationell skola" },
  en: { us: "With us", other: "Local or international school" },
  es: { us: "Con nosotros", other: "Colegio local o internacional" },
};

/** Jämförelse: staplade kort på mobil, tabell från md och uppåt. */
export function ComparisonTable({ rows, locale }: { rows: ComparisonRow[]; locale?: Locale }) {
  const urlLocale = useLocale();
  const loc = locale ?? urlLocale;
  const headers = COMPARISON_HEADERS[loc];
  return (
    <>
      <ul className="mt-8 space-y-4 md:hidden">
        {rows.map((c) => (
          <li key={c.row} className="rounded-xl border border-border bg-card p-5 shadow-soft">
            <p className="text-xs font-semibold tracking-[0.12em] text-muted-foreground uppercase">
              {c.row}
            </p>
            <p className="mt-3 text-sm font-medium text-primary">{headers.us}</p>
            <p className="text-sm leading-relaxed">{c.us}</p>
            <p className="mt-3 text-sm font-medium">{headers.other}</p>
            <p className="text-sm leading-relaxed text-muted-foreground">{c.other}</p>
          </li>
        ))}
      </ul>

      <div className="mt-8 hidden rounded-xl border border-border bg-card shadow-soft md:block">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-border text-xs tracking-[0.12em] text-muted-foreground uppercase">
            <tr>
              <th className="px-6 py-4"> </th>
              <th className="px-6 py-4 text-primary">{headers.us}</th>
              <th className="px-6 py-4">{headers.other}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.row} className="border-t border-border align-top">
                <th scope="row" className="px-6 py-4 font-semibold">
                  {c.row}
                </th>
                <td className="px-6 py-4">{c.us}</td>
                <td className="px-6 py-4 text-muted-foreground">{c.other}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

const ENROLL_TEXT: Record<Locale, { apply: string; fees: string }> = {
  sv: { apply: "Anmäl ditt barn", fees: "Se avgifter" },
  en: { apply: "Enrol your child", fees: "See fees" },
  es: { apply: "Solicita una plaza", fees: "Ver tarifas" },
};

/** Knappar till anmälan och avgifter – full bredd på mobil. */
export function EnrollButtons({
  className = "",
  locale,
}: {
  className?: string;
  locale?: Locale;
}) {
  const urlLocale = useLocale();
  const loc = locale ?? urlLocale;
  const t = ENROLL_TEXT[loc];
  return (
    <div className={`flex flex-col gap-3 sm:flex-row sm:items-center ${className}`}>
      <Link
        to={pathFor("/ansokan", loc) as never}
        className="flex min-h-11 items-center justify-center rounded-md bg-primary px-6 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
      >
        {t.apply}
      </Link>
      <Link
        to={pathFor("/avgifter", loc) as never}
        className="flex min-h-11 items-center justify-center rounded-md border border-border bg-card px-6 text-sm font-medium transition-colors hover:border-primary/40"
      >
        {t.fees}
      </Link>
    </div>
  );
}

export const ONBOARDING_TEXT: Record<Locale, string> = {
  sv: "Ingår om ni önskar för er som anmäler minst ett barn på heltid, minst en termin (inte vid korttidsplats). Ni kryssar i genomgången i anmälningsformuläret, och tidigast 15 dagar efter påskrivet avtal och genomförd betalning får ni en 45 minuters personlig genomgång – praktiska råd om flytten, skolgången och vardagen i Spanien. Bokas när det passar, på plats eller digitalt.",
  en: "Included if you'd like it, for families enrolling at least one child full-time for at least one term (not short stays). You tick the session in the application form, and at the earliest 15 days after the contract is signed and payment is completed you get a 45-minute personal briefing – practical advice on the move, schooling and everyday life in Spain. Held at a time that suits you, in person or online.",
  es: "Incluida si lo deseáis, para las familias que inscriben al menos a un alumno a jornada completa durante al menos un trimestre (no en estancias cortas). La solicitáis en el formulario de inscripción y, como pronto 15 días después de firmar el contrato y completar el pago, recibís una sesión personal de 45 minutos con consejos prácticos sobre la mudanza, la escolarización y la vida cotidiana en España. Presencial o en línea, cuando os convenga.",
};

const ONBOARDING_HEADING: Record<
  Locale,
  { included: string; fullTimeBadge: string; fullTimeNote: string; title: string; disclaimer: string; link: string }
> = {
  sv: {
    included: "Ingår",
    fullTimeBadge: "Gäller heltid",
    fullTimeNote:
      "Genomgången ingår för er som anmäler minst ett barn på heltid, en eller flera terminer – den ingår inte vid korttidsplats.",
    title: "45 minuters genomgång inför flytten",
    disclaimer:
      "Genomgången är allmän vägledning från skolan och ersätter inte juridisk eller ekonomisk rådgivning.",
    link: "Läs mer om genomgången",
  },
  en: {
    included: "Included",
    fullTimeBadge: "Full-time only",
    fullTimeNote:
      "The briefing is included for families enrolling at least one child full-time, for one or more terms – it is not included with a short stay.",
    title: "A 45-minute briefing before the move",
    disclaimer:
      "The briefing is general guidance from the school and does not replace legal or financial advice.",
    link: "Read more about the briefing",
  },
  es: {
    included: "Incluido",
    fullTimeBadge: "Solo jornada completa",
    fullTimeNote:
      "La sesión está incluida para las familias que inscriben al menos a un alumno a jornada completa, durante uno o más trimestres – no se incluye en estancias cortas.",
    title: "Una sesión antes de la mudanza",
    disclaimer: "La sesión ofrece orientación general del colegio y no sustituye el asesoramiento jurídico o financiero.",
    link: "Más información sobre la sesión",
  },
};

export function OnboardingNote({
  className = "",
  rounded = "rounded-xl",
  bordered = true,
  locale,
  fullTimeOnly = false,
}: {
  className?: string;
  /** Sätt t.ex. "rounded-none" när noten sitter ihop med annat innehåll. */
  rounded?: string;
  bordered?: boolean;
  locale?: Locale;
  /** True när besökaren bara valt korttid – rutan gråas ut och märks som heltidspecifik. */
  fullTimeOnly?: boolean;
}) {
  const urlLocale = useLocale();
  const loc = locale ?? urlLocale;
  const t = ONBOARDING_HEADING[loc];
  return (
    <div
      className={`${rounded} ${bordered ? "border border-border" : ""} bg-sand p-6 sm:p-7 ${fullTimeOnly ? "opacity-60" : ""} ${className}`}
    >
      <p className={`text-xs font-semibold tracking-[0.18em] uppercase ${fullTimeOnly ? "text-muted-foreground" : "text-primary"}`}>
        {fullTimeOnly ? t.fullTimeBadge : t.included}
      </p>
      <h3 className="mt-2 text-lg font-semibold">{t.title}</h3>
      {fullTimeOnly ? (
        <p className="mt-2 text-sm leading-relaxed font-medium">{t.fullTimeNote}</p>
      ) : null}
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{ONBOARDING_TEXT[loc]}</p>
      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
        {t.disclaimer}{" "}
        <a className="text-primary link-quiet" href={pathFor("/flyttgenomgang", loc)}>
          {t.link}
        </a>
        .
      </p>
    </div>
  );
}
